/**
 * App router + auth gate.
 *
 * Routing rules:
 *   /                            → redirect to /signin or workspace home
 *   /signin                      → SignIn (public)
 *   /signup                      → SignUp (public, role selection)
 *   /onboarding/ifa-verify       → IFAVerify (auth required, IFA only)
 *   /onboarding/pending          → ApprovalPending (auth + status=pending)
 *
 *   IFA workspace (auth + active + role=ifa)
 *   /clients                     → IFADashboard (populated book)
 *   /clients/empty               → IFAEmpty (day-zero)
 *   /clients/new                 → AddClient modal
 *   /clients/:clientId           → ClientDetail
 *
 *   Individual workspace (auth + active + role=individual)
 *   /home                        → InvestorHome (populated portfolio)
 *   /home/empty                  → IndividualEmpty (day-zero)
 *
 *   Briefing surfaces (either role)
 *   /brief/:briefingId/progress  → BriefingProgress (generation in flight)
 *   /brief/:briefingId           → BriefingViewer (full briefing)
 *
 * `RequireAuth` and `RequireActive` HOCs handle the gating. The auth
 * snapshot is live (onSnapshot inside AuthProvider), so when admin
 * flips a user's status from pending → active, the router re-evaluates
 * within ~100ms and the user lands on their workspace home without a
 * page refresh.
 */
import { type PropsWithChildren, Suspense, lazy, useEffect, useRef, useState } from "react";
import { BrowserRouter, Navigate, Route, Routes as RouterRoutes, useLocation } from "react-router-dom";
import { useAuth, AuthProvider } from "@/auth";
import { RecordsModeContext, useRecordsMode, recordsRouteAllowed, Routes, homeFor } from "@/lib/routes";
import { getProductCapabilities } from "@/lib/api";
import type { UserRole } from "@/types";
import { SignUp } from "@/screens/SignUp";
import { IFAVerify } from "@/screens/IFAVerify";
import { ApprovalPending } from "@/screens/ApprovalPending";
import { SignIn } from "@/screens/SignIn";

/**
 * RETAIL FREE TIER (2026-08-30).
 *
 * Retail used to be a hard paywall: sign in, get bounced straight to
 * /subscribe, never see a single number about your own money. Of the 11 retail
 * accounts ever created, 9 never wrote an app-data document at all — they
 * arrived, hit the wall, and left.
 *
 * The tracking layer is now free for everyone. It costs us essentially nothing
 * per user (deterministic, cached, precomputed snapshots) and it is the only
 * way anyone can find out whether the product is worth paying for. Money is
 * made on the AI actions, which stay metered server-side by tools/ai_budget —
 * a client route was never the real gate anyway (core/deps.require_retail_access
 * is).
 *
 * Set VITE_RETAIL_FREE_TIER="off" and rebuild to restore the hard paywall.
 */
const RETAIL_FREE_TIER =
  (import.meta.env.VITE_RETAIL_FREE_TIER as string | undefined)?.toLowerCase() !== "off";
import { Landing } from "@/screens/Landing";
import { NotFound } from "@/screens/NotFound";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { CreditsProvider } from "@/lib/credits";

function Splash() {
  return (
    <div
      style={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        background: "var(--paper-2)",
        color: "var(--ink-light)",
        fontFamily: "var(--sans)",
      }}
    >
      <div style={{ textAlign: "center" }}>
        <div
          className="bm"
          style={{ width: 36, height: 36, fontSize: 16, margin: "0 auto" }}
        >
          S
        </div>
        <div
          style={{
            marginTop: 16,
            fontFamily: "var(--mono)",
            fontSize: 11,
            letterSpacing: ".14em",
            textTransform: "uppercase",
          }}
        >
          Loading…
        </div>
      </div>
    </div>
  );
}


/** Shown when the profile read fails — the alternative was an endless spinner. */
function ProfileUnavailable() {
  return (
    <div role="alert" style={{ maxWidth: 460, margin: "18vh auto", padding: 20,
      borderRadius: 12, border: "1px solid var(--line)", background: "var(--paper-1)" }}>
      <div style={{ fontWeight: 600, marginBottom: 6 }}>We couldn&rsquo;t load your account</div>
      <div style={{ fontSize: 13, color: "var(--ink-mid)", marginBottom: 14 }}>
        Your data is safe — we just couldn&rsquo;t read your profile. This is
        usually temporary.
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        <button className="btn btn-primary" onClick={() => window.location.reload()}>Retry</button>
        <a className="btn btn-ghost" href="/signout">Sign out</a>
      </div>
    </div>
  );
}

function RequireAuth({ children }: PropsWithChildren) {
  const { user, loading } = useAuth();
  if (loading) return <Splash />;
  if (!user) return <Navigate to={Routes.SignIn} replace />;
  return <>{children}</>;
}

// Owner-only gate (client-side; the /admin/* API is also server-gated).
const ADMIN_EMAILS = ["murali.adhikarla@gmail.com"];
function RequireAdmin({ children }: PropsWithChildren) {
  const { user, profile, loading , profileUnavailable } = useAuth();
  if (loading) return <Splash />;
  if (!user) return <Navigate to={Routes.SignIn} replace />;
  // `profileUnavailable` was produced by AuthProvider and read by NOTHING, so a
  // failed profile read parked the user on a blank Splash forever — no message,
  // no retry, no sign-out. The flag exists precisely to avoid that; wire it.
  if (!profile) return profileUnavailable ? <ProfileUnavailable /> : <Splash />;
  if (!ADMIN_EMAILS.includes((profile.email || "").toLowerCase())) {
    return <Navigate to={homeFor(profile.role)} replace />;
  }
  return <>{children}</>;
}

function RequirePending({ children }: PropsWithChildren) {
  const { user, profile, loading , profileUnavailable } = useAuth();
  if (loading) return <Splash />;
  if (!user) return <Navigate to={Routes.SignIn} replace />;
  // `profileUnavailable` was produced by AuthProvider and read by NOTHING, so a
  // failed profile read parked the user on a blank Splash forever — no message,
  // no retry, no sign-out. The flag exists precisely to avoid that; wire it.
  if (!profile) return profileUnavailable ? <ProfileUnavailable /> : <Splash />;
  if (profile.status === "active") {
    return <Navigate to={homeFor(profile.role)} replace />;
  }
  // Retail never waits for manual approval. Under the free tier a pending
  // individual simply goes to their workspace; under the old hard paywall they
  // went to the paywall instead of the (irrelevant) approval screen.
  if (profile.role === "individual") {
    return <Navigate to={RETAIL_FREE_TIER ? homeFor(profile.role) : Routes.Subscribe} replace />;
  }
  return <>{children}</>;
}

/** Gate for /subscribe: reachable by an authenticated retail user whether their
 *  status is pending (not yet paid) or active-but-unsubscribed. Fully-Pro retail
 *  is bounced to their workspace; advisors see the 'coming soon' card. */
function SubscribeGate({ children }: PropsWithChildren) {
  const { user, profile, loading , profileUnavailable } = useAuth();
  if (loading) return <Splash />;
  if (!user) return <Navigate to={Routes.SignIn} replace />;
  // `profileUnavailable` was produced by AuthProvider and read by NOTHING, so a
  // failed profile read parked the user on a blank Splash forever — no message,
  // no retry, no sign-out. The flag exists precisely to avoid that; wire it.
  if (!profile) return profileUnavailable ? <ProfileUnavailable /> : <Splash />;
  // Paying retail users also need this route to manage or cancel auto-renewal.
  return <>{children}</>;
}

function RequireActive({
  roles,
  allowUnsub,
  children,
}: PropsWithChildren<{ roles?: UserRole[]; allowUnsub?: boolean }>) {
  const { user, profile, loading , profileUnavailable } = useAuth();
  if (loading) return <Splash />;
  if (!user) return <Navigate to={Routes.SignIn} replace />;
  // `profileUnavailable` was produced by AuthProvider and read by NOTHING, so a
  // failed profile read parked the user on a blank Splash forever — no message,
  // no retry, no sign-out. The flag exists precisely to avoid that; wire it.
  if (!profile) return profileUnavailable ? <ProfileUnavailable /> : <Splash />;
  if (profile.status !== "active") {
    // Retail has no approval queue. On the free tier `status` is irrelevant to
    // them — they get the deterministic product immediately and `status` flips
    // to active on their first payment. Under the old paywall they were sent to
    // /subscribe instead.
    if (profile.role === "individual") {
      // ROLE FIRST, always. This returned `children` before the `roles` check
      // below ever ran, so a PENDING individual on the free tier could mount
      // adviser-only screens while an ACTIVE one could not — activation state
      // was accidentally deciding role compatibility. Role is not a function of
      // status, and the two must be evaluated independently.
      if (roles && !roles.includes("individual")) {
        return <Navigate to={homeFor(profile.role)} replace />;
      }
      if (RETAIL_FREE_TIER) return <>{children}</>;
      return <Navigate to={Routes.Subscribe} replace />;
    }
    // Both advisor personas (IFA/RIA + MFD) capture a registration first.
    if ((profile.role === "ifa" || profile.role === "mfd" || profile.role === "ra") && !profile.sebiRegistration) {
      return <Navigate to={Routes.IFAVerify} replace />;
    }
    return <Navigate to={Routes.Pending} replace />;
  }
  if (roles && !roles.includes(profile.role)) {
    return <Navigate to={homeFor(profile.role)} replace />;
  }
  // The retail paywall. Under RETAIL_FREE_TIER an unsubscribed individual keeps
  // full access to the deterministic product (tracking, X-ray, market, alerts)
  // and is stopped only at the metered AI actions, by the server. With the flag
  // off this reverts to the old behaviour: any unsubscribed individual is routed
  // to /subscribe (which sets allowUnsub, so the screen stays reachable).
  if (!RETAIL_FREE_TIER && !allowUnsub && profile.role === "individual"
      && profile.subscribed !== true) {
    return <Navigate to={Routes.Subscribe} replace />;
  }
  return <>{children}</>;
}

/**
 * Landing decision at `/`:
 *   - Unauthenticated visitor → marketing landing page (Landing.tsx).
 *     "Get started" / "Sign in" CTAs route into the Pro auth flow.
 *   - Authenticated + active → workspace home (role-dependent).
 *   - Authenticated + pending (IFA missing SEBI doc) → IFAVerify.
 *   - Authenticated + pending (other) → Pending screen.
 *
 * The unauthed branch previously redirected straight to /signin, which
 * hid the value-prop / pricing / coverage page from cold traffic.
 */
function LandingRedirect() {
  const { user, profile, loading , profileUnavailable } = useAuth();
  if (loading) return <Splash />;
  if (!user) return <Landing />;
  // `profileUnavailable` was produced by AuthProvider and read by NOTHING, so a
  // failed profile read parked the user on a blank Splash forever — no message,
  // no retry, no sign-out. The flag exists precisely to avoid that; wire it.
  if (!profile) return profileUnavailable ? <ProfileUnavailable /> : <Splash />;
  if (profile.status === "active") {
    return <Navigate to={homeFor(profile.role)} replace />;
  }
  // Pending retail → straight into the product on the free tier (previously:
  // pay to activate). Advisors → their approval path.
  if (profile.role === "individual") {
    return <Navigate to={RETAIL_FREE_TIER ? homeFor(profile.role) : Routes.Subscribe} replace />;
  }
  if ((profile.role === "ifa" || profile.role === "mfd" || profile.role === "ra") && !profile.sebiRegistration) {
    return <Navigate to={Routes.IFAVerify} replace />;
  }
  return <Navigate to={Routes.Pending} replace />;
}

/**
 * /signout — actually signs out, then lands on the public sign-in.
 *
 * The pre-activation AppNav ("Sign out" on IFAVerify / sign-up screens) links
 * here via a plain href. Without this route the link fell through to the
 * catch-all `*` → LandingRedirect, and because the Firebase session was still
 * live, a pending advisor was bounced straight back into /onboarding/ifa-verify
 * — a sign-out that never signed out. We wait for auth to resolve, call
 * signOut() once, hold on a splash until the session clears, then redirect.
 */
function SignOut() {
  const { user, loading, signOut } = useAuth();
  const started = useRef(false);
  // F25: `void signOut()` discarded the rejection. If the call failed — offline,
  // a token-revocation error — `user` stayed set, so the guard below held the
  // reader on a Splash spinner with no message and no way out, forever. Signing
  // out is the one action a user takes when something is already wrong; it must
  // never be the thing that traps them.
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    if (loading || started.current) return;
    started.current = true;
    signOut().catch(() => setFailed(true));
  }, [loading, signOut]);

  if (failed) {
    return (
      <main style={{ minHeight: "100dvh", display: "grid", placeItems: "center", padding: 24, textAlign: "center" }}>
        <div style={{ maxWidth: 420 }}>
          <h1 style={{ fontSize: 20, marginBottom: 8 }}>Couldn&rsquo;t sign you out</h1>
          <p className="muted" style={{ fontSize: 14, lineHeight: 1.55 }}>
            You are still signed in on this device. Check your connection and try
            again — if it keeps failing, closing the browser ends the session.
          </p>
          <button
            type="button"
            className="btn primary"
            style={{ marginTop: 18, minHeight: 44 }}
            onClick={() => {
              setFailed(false);
              started.current = false;
            }}
          >
            Try again
          </button>
        </div>
      </main>
    );
  }
  if (loading || user) return <Splash />;
  return <Navigate to={Routes.SignIn} replace />;
}

/**
 * F32. Until 2026-09-05 the ONLY <ErrorBoundary> in the app was the one at the
 * root in main.tsx, so a render error anywhere — a single analytics panel
 * reading a field the server stopped sending — unmounted the entire tree. The
 * boundary's own docstring says its job is to turn "the app is gone" into "this
 * panel broke, here is a retry"; mounted only at the root it could not do that,
 * because the root IS the app.
 *
 * This wraps the routed screen. A broken screen now fails alone, keeps the
 * error inside itself, clears automatically when the reader navigates
 * elsewhere (resetKey), and offers an in-place retry before a full reload.
 * The root boundary stays as the last resort for anything outside the router.
 */
function RoutedBoundary({ children }: PropsWithChildren) {
  const { pathname } = useLocation();
  return (
    <ErrorBoundary label={`route:${pathname}`} resetKey={pathname} hideReload={false}>
      {children}
    </ErrorBoundary>
  );
}


/**
 * F29 — route-level code splitting.
 *
 * Every screen was a static import, so the first paint of the marketing page
 * downloaded and parsed the client book, the report viewer, the market screen,
 * the admin usage dashboard and the billing flows — 1.59 MB in one chunk
 * (427 kB gzipped), which the build has been warning about on every run. A
 * logged-out visitor paid for the entire adviser workspace to read a headline.
 *
 * The pre-auth funnel (Landing, SignIn, SignUp, the onboarding screens, 404)
 * stays EAGER on purpose: it is the first thing cold traffic paints, so a
 * chunk round-trip there would add latency exactly where it costs most. The
 * gating HOCs are eager too, so a redirect still resolves without fetching the
 * screen it is redirecting away from.
 *
 * Everything behind auth loads on demand, under the <Suspense> in AppRoutes.
 */
const AddClient = lazy(() => import("@/screens/AddClient").then((m) => ({ default: m.AddClient })));
const AdminUsage = lazy(() => import("@/screens/AdminUsage").then((m) => ({ default: m.AdminUsage })));
const AdviserPlans = lazy(() => import("@/screens/AdviserPlans").then((m) => ({ default: m.AdviserPlans })));
const AlertsScreen = lazy(() => import("@/screens/AlertsScreen").then((m) => ({ default: m.AlertsScreen })));
const BriefingDemoViewer = lazy(() => import("@/screens/BriefingDemoViewer").then((m) => ({ default: m.BriefingDemoViewer })));
const BriefingProgress = lazy(() => import("@/screens/BriefingProgress").then((m) => ({ default: m.BriefingProgress })));
const BriefingViewer = lazy(() => import("@/screens/BriefingViewer").then((m) => ({ default: m.BriefingViewer })));
const ClientDetail = lazy(() => import("@/screens/ClientDetail").then((m) => ({ default: m.ClientDetail })));
const IFADashboard = lazy(() => import("@/screens/IFADashboard").then((m) => ({ default: m.IFADashboard })));
const IFAEmpty = lazy(() => import("@/screens/IFAEmpty").then((m) => ({ default: m.IFAEmpty })));
const InboxScreen = lazy(() => import("@/screens/InboxScreen").then((m) => ({ default: m.InboxScreen })));
const IndividualEmpty = lazy(() => import("@/screens/IndividualEmpty").then((m) => ({ default: m.IndividualEmpty })));
const InvestorHome = lazy(() => import("@/screens/InvestorHome").then((m) => ({ default: m.InvestorHome })));
const MFDFundsScreen = lazy(() => import("@/screens/MFDFundsScreen").then((m) => ({ default: m.MFDFundsScreen })));
const MarketScreen = lazy(() => import("@/screens/MarketScreen").then((m) => ({ default: m.MarketScreen })));
const PackPicker = lazy(() => import("@/screens/PackPicker").then((m) => ({ default: m.PackPicker })));
const PaymentSuccess = lazy(() => import("@/screens/PaymentSuccess").then((m) => ({ default: m.PaymentSuccess })));
const PracticeScreen = lazy(() => import("@/screens/PracticeScreen").then((m) => ({ default: m.PracticeScreen })));
const ProspectScreen = lazy(() => import("@/screens/ProspectScreen").then((m) => ({ default: m.ProspectScreen })));
const ReportsScreen = lazy(() => import("@/screens/ReportsScreen").then((m) => ({ default: m.ReportsScreen })));
const SubscribePlans = lazy(() => import("@/screens/SubscribePlans").then((m) => ({ default: m.SubscribePlans })));
const WatchlistScreen = lazy(() => import("@/screens/WatchlistScreen").then((m) => ({ default: m.WatchlistScreen })));
const LibraryScreen = lazy(() => import("@/screens/LibraryScreen").then((m) => ({ default: m.LibraryScreen })));
const LibraryItemScreen = lazy(() => import("@/screens/LibraryItemScreen").then((m) => ({ default: m.LibraryItemScreen })));
const ComplianceHome = lazy(() => import("@/screens/ComplianceHome").then((m) => ({ default: m.ComplianceHome })));
const ClientCompliance = lazy(() => import("@/screens/ClientCompliance").then((m) => ({ default: m.ClientCompliance })));
const ClientAck = lazy(() => import("@/screens/ClientAck").then((m) => ({ default: m.ClientAck })));

export function AppRoutes() {
  const { profile, loading } = useAuth();
  const [mode, setMode] = useState<boolean | null>(null);
  useEffect(() => {
    if (loading) return;
    const professional = profile?.role === "ifa" || profile?.role === "mfd";
    if (!professional) { setMode(false); return; }
    let active = true;
    getProductCapabilities().then(c => { if (active) setMode(c.professional_records === true); })
      .catch(() => { if (active) setMode(true); });
    return () => { active = false; };
  }, [loading, profile?.role]);
  if (loading || mode === null) return <Splash />;
  return <RecordsModeContext.Provider value={mode}>
    <CreditsProvider enabled={!mode}><ConfiguredRoutes /></CreditsProvider>
  </RecordsModeContext.Provider>;
}

export function ConfiguredRoutes() {
  const records = useRecordsMode();
  const { pathname } = useLocation();
  const { profile } = useAuth();
  if (records && pathname === Routes.Subscribe) return <Navigate to="/plan" replace />;
  if (records && !recordsRouteAllowed(pathname)) {
    return <Navigate to={profile ? homeFor(profile.role) : "/"} replace />;
  }
  return (
    <RoutedBoundary>
    <Suspense fallback={<Splash />}>
    <RouterRoutes>
      <Route path="/" element={<LandingRedirect />} />
      <Route path="/signout" element={<SignOut />} />
      <Route path={Routes.SignUp} element={<SignUp />} />
      <Route path={Routes.SignIn} element={<SignIn />} />
      {/* The CLIENT's acknowledgement page: public on purpose. The signed token
          in the URL is the only credential, and it resolves one request once. */}
      <Route path={Routes.ClientAck} element={<ClientAck />} />

      <Route
        path={Routes.IFAVerify}
        element={
          <RequireAuth>
            <IFAVerify />
          </RequireAuth>
        }
      />
      <Route
        path={Routes.Pending}
        element={
          <RequirePending>
            <ApprovalPending />
          </RequirePending>
        }
      />

      {/* IFA workspace */}
      <Route
        path={Routes.IFAHome}
        element={
          <RequireActive roles={["ifa", "mfd", "ra"]}>
            <IFADashboard />
          </RequireActive>
        }
      />
      <Route
        path={Routes.IFAEmpty}
        element={
          <RequireActive roles={["ifa", "mfd", "ra"]}>
            {records ? <IFADashboard /> : <IFAEmpty />}
          </RequireActive>
        }
      />
      <Route
        path={Routes.AddClient}
        element={
          <RequireActive roles={["ifa", "mfd", "ra"]}>
            <AddClient />
          </RequireActive>
        }
      />
      <Route
        path={Routes.ClientDetail}
        element={
          <RequireActive roles={["ifa", "mfd", "ra"]}>
            <ClientDetail />
          </RequireActive>
        }
      />
      {/* Investment Adviser compliance: IFA only here; the server additionally
          requires a SEBI registration on file. */}
      <Route path={Routes.ClientCompliance} element={<RequireActive roles={["ifa"]}><ClientCompliance /></RequireActive>} />
      <Route path={Routes.Compliance} element={<RequireActive roles={["ifa"]}><ComplianceHome /></RequireActive>} />

      {/* Individual workspace.
          Note: NO `role` restriction here — IFAs also need to access
          /home via their "My portfolio" persona toggle in AppNav.
          The screen reads /portfolio for the CURRENT user (whoever
          is signed in), so both personas see their own data. */}
      <Route
        path={Routes.IndividualHome}
        element={
          <RequireActive>
            <InvestorHome />
          </RequireActive>
        }
      />
      <Route
        path={Routes.IndividualEmpty}
        element={
          <RequireActive>
            <IndividualEmpty />
          </RequireActive>
        }
      />

      {/* Adviser plan / subscription — MFD + IFA */}
      <Route path="/plan" element={<RequireActive roles={["ifa", "mfd", "ra"]}><AdviserPlans /></RequireActive>} />

      {/* Admin — AI usage tracker (owner-only; server + client gated) */}
      <Route path="/admin" element={<RequireAdmin><AdminUsage /></RequireAdmin>} />

      {/* Fund research workspace — MFD + IFA. Discover / compare / AI fund note. */}
      <Route
        path={Routes.Funds}
        element={
          <RequireActive roles={["ifa", "mfd", "ra"]}>
            <MFDFundsScreen />
          </RequireActive>
        }
      />

      {/* Cross-role surfaces (either role) */}
      <Route
        path={Routes.Alerts}
        element={
          <RequireActive>
            <AlertsScreen />
          </RequireActive>
        }
      />
      <Route
        path={Routes.Inbox}
        element={
          <RequireActive>
            <InboxScreen />
          </RequireActive>
        }
      />
      <Route
        path={Routes.Watchlist}
        element={
          <RequireActive>
            <WatchlistScreen />
          </RequireActive>
        }
      />
      <Route path={Routes.Library} element={<RequireActive><LibraryScreen /></RequireActive>} />
      <Route path={Routes.LibraryItem} element={<RequireActive><LibraryItemScreen /></RequireActive>} />
      <Route
        path={Routes.Reports}
        element={
          <RequireActive>
            <ReportsScreen />
          </RequireActive>
        }
      />
      <Route
        path={Routes.Practice}
        element={
          <RequireActive>
            <PracticeScreen />
          </RequireActive>
        }
      />
      <Route
        path={Routes.Prospect}
        element={
          <RequireActive>
            <ProspectScreen />
          </RequireActive>
        }
      />
      <Route
        path={Routes.Market}
        element={
          <RequireActive>
            <MarketScreen />
          </RequireActive>
        }
      />

      {/* Briefing surfaces — ADVISOR-ONLY. The AI brief carries conviction +
          action plans; under our design retail gets only the deterministic,
          no-suggestions "View analytics" workspace, never the brief. role="ifa"
          also covers the IFA's My-portfolio persona (role is unchanged by the
          persona toggle), so an advisor still reaches briefs from either side.
          A retail (individual) account hitting /brief/:ticker by URL is
          redirected to its home. The static /brief/demo sample stays open. */}
      <Route
        path={Routes.BriefingProgress}
        element={
          <RequireActive roles={["ifa", "ra"]}>
            <BriefingProgress />
          </RequireActive>
        }
      />
      {/* /brief/demo — BEHIND AUTH as of 2026-09-05, deliberately.
          It was fully public, and its content is not a neutral sample: on a
          named NSE stock it renders a "hold" verdict pill, "+11% upside ·
          12-month horizon · R:R", a section headed "Investment view &
          recommendation" (verdict · rationale · position sizing), an "Action
          plan", and price targets. Published to anonymous visitors by an entity
          that is not a SEBI-registered RA or IA, while the landing page on the
          same domain states "No buy, sell, or target language anywhere in the
          product" — the page was its own counter-evidence. It also breaks the
          locked Path-2 rule that action verbs stay internal.
          The landing CTA has been removed alongside this. The authenticated
          links (ApprovalPending, IFAEmpty, IndividualEmpty) still work.
          Reopening it needs the descriptive-only rewrite, not a flag flip. */}
      <Route
        path="/brief/demo"
        element={
          <RequireAuth>
            <BriefingDemoViewer />
          </RequireAuth>
        }
      />
      <Route
        path={Routes.BriefingViewer}
        element={
          <RequireActive roles={["ifa", "ra"]}>
            <BriefingViewer />
          </RequireActive>
        }
      />

      {/* /subscribe — retail Pro paywall (SubscribePlans + PayU checkout).
          allowUnsub keeps it reachable for an unpaid individual (RequireActive's
          retail paywall otherwise redirects HERE). Advisors still buy credits at
          /billing/packs via the Credits pill; they see a 'coming soon' card here. */}
      <Route
        path={Routes.Subscribe}
        element={
          <SubscribeGate>
            <SubscribePlans />
          </SubscribeGate>
        }
      />
      {/* Briefing-credit packs. IFA-only: equity briefings are the only thing
          these credits buy, and generation is gated to role=ifa on every path,
          so selling them to anyone else is selling a credit that cannot be
          spent. Retail's AI credits are a different wallet, bought in-context. */}
      <Route
        path={Routes.BillingPacks}
        element={
          <RequireActive roles={["ifa", "ra"]}>
            <PackPicker />
          </RequireActive>
        }
      />
      <Route
        path={Routes.BillingSuccess}
        element={
          <RequireActive>
            <PaymentSuccess />
          </RequireActive>
        }
      />

      {/* /landing — explicit access to the marketing page from inside
          the app (also entry-point for unauthed via /). The route is
          open to everyone so authed users can revisit the page (e.g.
          to share, re-read pitch). */}
      <Route path="/landing" element={<Landing />} />

      {/* F36: a wrong URL now SAYS so instead of silently landing you home. */}
      <Route path="*" element={<NotFound />} />
    </RouterRoutes>
    </Suspense>
    </RoutedBoundary>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider><AppRoutes /></AuthProvider>
    </BrowserRouter>
  );
}
